import React, { useEffect } from "react";
import Card from "components/card";
import Pagination from "@material-ui/lab/Pagination";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BiEditAlt } from 'react-icons/bi';
import { CiImageOff } from "react-icons/ci";
import { MdDelete } from "react-icons/md";

function CompanyTable(props) {
  const {
    tableData,
    handleItemsPerPageChange,
    handlePageChange,
    currentPage,
    totalCount,
    itemsPerPage,
    setEditCompanyDetails,
    handleDeleteCompany
  } = props;

  // Add columnHelper here
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("logo", {
      id: "logo",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          LOGO
        </p>
      ),
      cell: (info) => {
         const logoUrl = info?.getValue();
          return logoUrl ? (
            <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-md border">
              <img
                src={logoUrl}
                alt="Company Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
          ) : (
            <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-md border text-gray-400">
              <CiImageOff size={20} />
            </div>
          );
      },
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          COMPANY NAME
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          EMAIL
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("website", {
      id: "website",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
         WEBSITE
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("linkedin", {
      id: "linkedin",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">LINKEDIN</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
      columnHelper.accessor("about", {
      id: "about",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ABOUT</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("startupStage", {
      id: "startupStage",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">STARTUP STAGE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info?.row?.original?.startupStage?.name ?? "-"}
        </p>
      ),
    }),
    columnHelper.accessor("industry", {
      id: "industry",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          INDUSTRY
        </p>
      ),
      cell: (info) => {
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info?.row?.original?.industry?.name ?? "-"}
          </p>
        )
      },
    }),
    columnHelper.accessor("haveRaisedCapital", {
      id: "haveRaisedCapital",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">RAISED CAPITAL</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()?"Yes":"No"}
        </p>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">CREATED AT</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {new Date(info?.getValue())?.toLocaleString()}
        </p>
      ),
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          ACTIONS
        </p>
      ),
      cell: (data) => (
        <div className="flex justify-start gap-4">
          <div className="flex space-x-4 justify-start text-yellow-500 text-xl cursor-pointer" onClick={() => {
            setEditCompanyDetails(data?.row?.original);
          }}>
            <BiEditAlt/>
          </div>
          <div className="flex space-x-4 justify-start text-red-500 text-xl cursor-pointer" onClick={() => {
            const confirmed = window.confirm("Are you sure you want to delete this company?");
            if (confirmed) {
              handleDeleteCompany(data?.row?.original?._id);
            }
          }}>
            <MdDelete />
          </div>
        </div>
      ),
    }),
  ];

  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = React.useState(() => [...tableData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  useEffect(() => {
    setData(tableData)
  }, [tableData])

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() ? (
                          <span>
                            {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                          </span>
                        ) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table
              .getRowModel()
              .rows.slice(0)
              .map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className="min-w-[150px] border-white/0 py-3 pr-4"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>

        <div className="mt-4 flex items-center justify-between">
          <Pagination
            count={Math.ceil(totalCount / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            size="small"
            showFirstButton
            showLastButton
          />
          <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </Card>
  );
}

export default CompanyTable;
